import React, { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { themeColors } from 'themes/clients/baseTheme';

import { Individual, ObjectiveTypeCompany, DepartmentThin, TeamFilled } from 'assets/assetsV1/iconComponents';

import { OBJECTIVE_LEVEL } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { SelectV2 } from 'uiToolkitV1/SelectV2';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const DepartmentThinIcon = () => <DepartmentThin fill="none" />;

const ObjectiveLevel = ({
  objectiveLevel,
  onChange,
  isReadOnly,
  disabledLevels = [],
  allowedLevels = [
    OBJECTIVE_LEVEL.COMPANY,
    OBJECTIVE_LEVEL.DEPARTMENT,
    OBJECTIVE_LEVEL.TEAM,
    OBJECTIVE_LEVEL.INDIVIDUAL,
  ],
}) => {
  const { moduleName } = useOrganisationSettings();

  const getObjectiveLevelIcon = (curObjectiveLevel) => {
    switch (curObjectiveLevel) {
      case 'COMPANY': {
        return ObjectiveTypeCompany;
      }
      case 'DEPARTMENT': {
        return DepartmentThinIcon;
      }
      case 'TEAM': {
        return TeamFilled;
      }
      case 'INDIVIDUAL': {
        return Individual;
      }
      default:
        return null;
    }
  };

  // TeamFilled

  const { menuItems, allLevels } = useMemo(() => {
    const allLevelsList = [
      {
        value: 'COMPANY',
        label: 'Company',
        description: `Organizational ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )} guiding the company’s strategic direction`,
        Icon: getObjectiveLevelIcon('COMPANY'),
      },
      {
        value: 'DEPARTMENT',
        label: 'Department',
        description: `Functional ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )} aligning departmental efforts and outcomes`,
        Icon: getObjectiveLevelIcon('DEPARTMENT'),
      },
      {
        value: 'TEAM',
        label: 'Team',
        description: `Collaborative ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )} driving the team’s collective achievements`,
        Icon: getObjectiveLevelIcon('TEAM'),
      },
      {
        value: 'INDIVIDUAL',
        label: 'Individual',
        description: `Personal ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )} guiding individual performance and growth`,
        Icon: getObjectiveLevelIcon('INDIVIDUAL'),
      },
    ];

    return {
      allLevels: allLevelsList,
      menuItems: allLevelsList
        .filter(({ value }) => allowedLevels.includes(value))
        .map((levelObj) => ({
          ...levelObj,
          isDisabled: disabledLevels.includes(levelObj?.value),
        })),
    };
  }, [moduleName, allowedLevels, disabledLevels]);

  const selectedItem = allLevels.find(({ value }) => value === objectiveLevel);

  return (
    <SelectV2
      isReadOnly={isReadOnly || allowedLevels?.length === 0}
      menuItems={menuItems}
      selectedItem={selectedItem}
      renderControlItem={(menuItem) => (
        <Box display="flex" gap="8px">
          <menuItem.Icon boxSize="18px" transform="none!important" color={themeColors.neutral[666666]} />
          <TextBodyMedium> {menuItem?.label} </TextBodyMedium>{' '}
        </Box>
      )}
      renderMenuItem={(menuItem) => (
        <Box display="flex" padding="4px 0px" alignItems="flex-start" alignSelf="stretch">
          <Box display="flex" padding="4px" justifyContent="center" alignItems="center">
            <menuItem.Icon boxSize="24px" color={themeColors.neutral[666666]} />
          </Box>

          <Box display="flex" padding="4px" flexDirection="column" alignItems="flex-start" gap="2px" flex="1 0 0">
            <TextBodyMedium bold color="button.blue.hover">
              {menuItem?.label}
            </TextBodyMedium>
            <TextLabel color="text.g2">{menuItem?.description}</TextLabel>
          </Box>
        </Box>
      )}
      onSelect={({ value }) => onChange(value)}
    />
  );
};

export default ObjectiveLevel;
